import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { responsiveVideoType } from '../propTypes';
import { isCurrentBreakpointAtLeast, isSoundMuted } from '../selectors';
import selectBestVideoSize from '../helpers/selectBestVideoSize';
import getResponsiveAlternative from '../helpers/getResponsiveAlternative';
import isScrolledIntoView from '../helpers/isScrolledIntoView';
import { toggleVideoSound } from '../actions';
import isFullScreen from '../helpers/isFullScreen';

/**
 * Calls play() on the passed HTMLMediaElement instance, and swallows promise rejections that can
 * occur if the video was already playing - in other words, makes it safe to call play() without
 * first checking whether the video was already playing.
 */
function safePlay(video) {
  const promise = video.play();
  if (promise && promise.catch) {
    promise.catch(() => {}); // ignore playback errors
  }
}

class Video extends PureComponent {
  constructor(props, context) {
    super(props, context);
    const {
      isSmall,
      video,
      viewportWidth,
      viewportHeight,
    } = props;
    this.state = {
      isVideoPlaying: false,
      visible: false,
      ...selectBestVideoSize(video, isSmall, viewportWidth, viewportHeight),
    };
  }

  componentDidMount() {
    this.checkBounds();
  }

  componentDidUpdate(prevProps, prevState) {
    const { paused } = this.props;
    const { alternative, index, visible } = this.state;
    if (paused !== prevProps.paused) {
      this.onPausedChanged();
    } else if (visible !== prevState.visible) {
      this.onVisibleChanged();
    } else if (alternative !== prevState.alternative || index !== prevState.index) {
      this.onVideoIndexChanged();
    }
    this.checkViewportSize(prevProps);
  }

  componentWillUnmount() {
    this.isunmounted = true;
  }

  onVisibleChanged() {
    const { paused, autoPlay = true } = this.props;
    const { visible } = this.state;
    if (!paused) {
      if (visible && autoPlay) {
        safePlay(this.videoEl);
      } else {
        this.videoEl.pause();
      }
    }
  }

  onPausedChanged() {
    const { paused, restartOnPause } = this.props;
    const { visible } = this.state;
    if (paused) {
      this.videoEl.pause();
      if (restartOnPause) {
        setTimeout(this.resetOnTimeout, 1000);
      }
    } else if (visible) {
      safePlay(this.videoEl);
    }
  }

  onVideoIndexChanged() {
    const { paused, currentTime } = this.props;
    if (!paused) {
      try {
        this.videoEl.currentTime = currentTime;
      } catch (e) {
        // ignore InvalidStateError in IE11
      }
      safePlay(this.videoEl);
    }
  }

  onVideoEnded = () => {
    const { loop, onEnded } = this.props;
    if (onEnded && !loop) {
      onEnded();
    }
  };

  onVideoPlaying = () => {
    this.setState({ isVideoPlaying: true });
    const { onPlaying } = this.props;
    if (onPlaying) {
      onPlaying(this.videoEl.duration);
    }
  };

  onVideoPaused = () => {
    // this.setState({ isVideoPlaying: false });
    const { onPaused } = this.props;
    if (onPaused) {
      onPaused();
    }
  };

  onVolumeChange = () => {
    const { onVideoMuted, onVideoUnmuted } = this.props;
    if (isFullScreen()) {
      if (this.videoEl.muted) {
        onVideoMuted();
      } else {
        onVideoUnmuted();
      }
    }
  };

  onVideoTimeUpdate = () => {
    const { onProgress } = this.props;
    if (onProgress) {
      onProgress(this.videoEl.currentTime);
    }
  };

  getCurrentVideo() {
    const { alternative, index } = this.state;
    const { video } = this.props;
    return getResponsiveAlternative(video, alternative).sizes[index];
  }

  setRef = el => {
    const { setVideoRef } = this.props;
    if (el) {
      this.videoEl = el;
    }
    if (setVideoRef) {
      setVideoRef(el);
    }
  };

  resetOnTimeout = () => {
    const { visible } = this.state;
    if (!visible) {
      try {
        this.videoEl.currentTime = 0;
      } catch (e) {
        // ignore InvalidStateError in IE11
      }
    }
  };

  checkBounds = () => {
    const visibilityRatio = 0.65;
    if (!this.isunmounted) {
      const { visible } = this.state;

      if (isScrolledIntoView(this.videoEl, visibilityRatio)) {
        if (!visible) {
          this.setState({ visible: true });
        }
      } else if (visible) {
        this.setState({ visible: false });
      }
      window.requestAnimationFrame(this.checkBounds); // eslint-disable-line no-undef
    }
  };

  checkViewportSize(prevProps) {
    const { viewportWidth, viewportHeight } = this.props;
    if (viewportWidth !== prevProps.viewportWidth || viewportHeight !== prevProps.viewportHeight) {
      const { isSmall, video } = this.props;
      const nextState = {
        ...selectBestVideoSize(video, isSmall, viewportWidth, viewportHeight),
      };

      const prevState = this.state;
      if (nextState.alternative === prevState.alternative) {
        // only ever go up in size, there's no reason to go down
        nextState.index = Math.max(prevState.index || 0, nextState.index);
      }
      this.setState(nextState);
    }
  }

  calculateVideoStyle() {
    const { isVideoPlaying } = this.state;
    const { width: videoWidth, height: videoHeight } = this.getCurrentVideo();
    const { viewportWidth, viewportHeight } = this.props;

    let scaledWidth = viewportWidth;
    let scale = scaledWidth / videoWidth;
    let scaledHeight = videoHeight * scale;
    if (scaledHeight < viewportHeight) {
      scaledHeight = viewportHeight;
      scale = scaledHeight / videoHeight;
      scaledWidth = videoWidth * scale;
    }

    return {
      width: Math.ceil(scaledWidth),
      height: Math.ceil(scaledHeight),
      left: (viewportWidth - scaledWidth) / 2,
      top: (viewportHeight - scaledHeight) / 2,
      opacity: isVideoPlaying ? 1 : 0,
    };
  }

  render() {
    const {
      className, loop, muted, playButtonClicked, onSpacePressed, onVideoPlay,
    } = this.props;
    const video = this.getCurrentVideo();
    const videoStyle = this.calculateVideoStyle();
    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          preload="none"
          src={video.url}
          autoPlay={false}
          loop={loop}
          muted={!playButtonClicked || muted}
          playsInline
          onPlaying={this.onVideoPlaying}
          onTimeUpdate={this.onVideoTimeUpdate}
          onPause={this.onVideoPaused}
          onEnded={this.onVideoEnded}
          className={className}
          ref={this.setRef}
          style={videoStyle}
          tabIndex={playButtonClicked ? 0 : -1}
          onKeyDown={onSpacePressed}
          onPlay={onVideoPlay}
          onVolumeChange={this.onVolumeChange}
        />
      </>
    );
  }
}

Video.propTypes = {
  className: PropTypes.string,
  currentTime: PropTypes.number,
  loop: PropTypes.bool,
  isSmall: PropTypes.bool.isRequired,
  onEnded: PropTypes.func,
  onPlaying: PropTypes.func,
  onProgress: PropTypes.func,
  onPaused: PropTypes.func,
  paused: PropTypes.bool,
  video: responsiveVideoType,
  viewportHeight: PropTypes.number.isRequired,
  viewportWidth: PropTypes.number.isRequired,
  muted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  restartOnPause: PropTypes.bool,
  setVideoRef: PropTypes.func,
  playButtonClicked: PropTypes.bool,
  onSpacePressed: PropTypes.func,
  onVideoPlay: PropTypes.func,
  onVideoMuted: PropTypes.func,
  onVideoUnmuted: PropTypes.func,
};

Video.defaultProps = {
  className: undefined,
  currentTime: 0,
  loop: false,
  paused: false,
  onEnded: undefined,
  onPlaying: undefined,
  onProgress: undefined,
  onPaused: undefined,
  video: null,
  muted: true,
  autoPlay: true,
  restartOnPause: true,
  setVideoRef: undefined,
  playButtonClicked: true,
  onSpacePressed: undefined,
  onVideoPlay: undefined,
  onVideoMuted: undefined,
  onVideoUnmuted: undefined,
};

const mapStateToProps = state => ({
  isSmall: !isCurrentBreakpointAtLeast(state, 'md'),
  muted: isSoundMuted(state),
});

const mapDispatchToProps = dispatch => ({
  onVideoMuted: () => dispatch(toggleVideoSound(true)),
  onVideoUnmuted: () => dispatch(toggleVideoSound(false)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Video);
